/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — DataTable row-selection state.
 *
 * Works controlled (pass `selectedRowKeys`) or uncontrolled (omit it), mirroring
 * the pattern the other DS form components use.
 *
 * Every handler returned here is stable for the component's lifetime. Rows are
 * memoised, and a handler that changed identity on each selection would be a new
 * prop on every row — so ticking one checkbox would re-render the whole page of
 * rows. Current values are therefore read through refs rather than closed over.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DataTableSelectionMode } from '../../types';

interface UseDataTableSelectionParams<TData> {
  data: TData[];
  getRowKey: (row: TData, index: number) => string;
  /** `false` when no selection column is rendered. */
  mode: DataTableSelectionMode | false;
  selectedRowKeys?: string[];
  defaultSelectedRowKeys?: string[];
  onSelectionChange?: (selectedRowKeys: string[], selectedRows: TData[]) => void;
  isRowSelectable?: (row: TData) => boolean;
}

/** Aggregate state of the header select-all checkbox. */
export type SelectAllState = 'none' | 'some' | 'all';

interface PageEntry<TData> {
  row: TData;
  key: string;
  selectable: boolean;
}

export const useDataTableSelection = <TData>({
  data,
  getRowKey,
  mode,
  selectedRowKeys,
  defaultSelectedRowKeys,
  onSelectionChange,
  isRowSelectable,
}: UseDataTableSelectionParams<TData>) => {
  const [uncontrolledKeys, setUncontrolledKeys] = useState<string[]>(defaultSelectedRowKeys ?? []);

  const isControlled = selectedRowKeys !== undefined;
  const selectedKeys = isControlled ? selectedRowKeys : uncontrolledKeys;

  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  /** Keys for the current page, resolved once so nothing needs an index lookup. */
  const pageEntries = useMemo<PageEntry<TData>[]>(
    () =>
      data.map((row, index) => ({
        row,
        key: getRowKey(row, index),
        selectable: !isRowSelectable || isRowSelectable(row),
      })),
    [data, getRowKey, isRowSelectable]
  );

  const selectableKeys = useMemo(() => pageEntries.filter((entry) => entry.selectable).map((entry) => entry.key), [
    pageEntries,
  ]);

  /*
   * Everything the stable handlers below need to read at call time. Writing to a
   * ref in an effect rather than during render keeps render side-effect free.
   */
  const latest = useRef({
    selectedKeys,
    selectedKeySet,
    pageEntries,
    selectableKeys,
    isControlled,
    onSelectionChange,
    mode,
  });
  useEffect(() => {
    latest.current = {
      selectedKeys,
      selectedKeySet,
      pageEntries,
      selectableKeys,
      isControlled,
      onSelectionChange,
      mode,
    };
  });

  /**
   * Anchor for shift-click range selection: the row whose checkbox was last
   * toggled without shift. Mirrors how a file list behaves.
   */
  const rangeAnchorKey = useRef<string | null>(null);

  const commit = useCallback((nextKeys: string[]) => {
    const { isControlled: controlled, onSelectionChange: notify, pageEntries: entries } = latest.current;

    if (!controlled) {
      setUncontrolledKeys(nextKeys);
    }

    if (notify) {
      const nextKeySet = new Set(nextKeys);
      // Only rows on the current page can be resolved — the table never sees the
      // rows behind keys selected on another page.
      const nextRows = entries.filter((entry) => nextKeySet.has(entry.key)).map((entry) => entry.row);
      notify(nextKeys, nextRows);
    }
  }, []);

  const toggleRow = useCallback(
    (key: string, options: { shiftKey: boolean } = { shiftKey: false }) => {
      const {
        selectedKeys: current,
        selectedKeySet: currentSet,
        selectableKeys: pageSelectable,
        mode: currentMode,
      } = latest.current;

      if (currentMode === 'single') {
        // Radios cannot be unticked, so a repeat click is a no-op rather than a
        // deselect. Selecting a row replaces the selection outright.
        if (currentSet.has(key)) return;

        rangeAnchorKey.current = key;
        commit([key]);
        return;
      }

      const anchor = rangeAnchorKey.current;
      const anchorIndex = anchor === null ? -1 : pageSelectable.indexOf(anchor);
      const targetIndex = pageSelectable.indexOf(key);

      /*
       * Shift-click extends from the anchor to the clicked row, matching the
       * clicked row's resulting state across the whole range. Both ends must be
       * on this page for a range to make sense; otherwise it degrades to a
       * single toggle.
       */
      if (options.shiftKey && anchorIndex !== -1 && targetIndex !== -1 && anchorIndex !== targetIndex) {
        const [from, to] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
        const rangeKeys = pageSelectable.slice(from, to + 1);
        const selecting = !currentSet.has(key);

        if (selecting) {
          const merged = [...current];
          rangeKeys.forEach((rangeKey) => {
            if (!currentSet.has(rangeKey)) merged.push(rangeKey);
          });
          commit(merged);
        } else {
          const rangeSet = new Set(rangeKeys);
          commit(current.filter((selectedKey) => !rangeSet.has(selectedKey)));
        }

        return;
      }

      rangeAnchorKey.current = key;

      commit(currentSet.has(key) ? current.filter((selectedKey) => selectedKey !== key) : [...current, key]);
    },
    [commit]
  );

  const selectAllState: SelectAllState = useMemo(() => {
    if (selectableKeys.length === 0) return 'none';

    const selectedCount = selectableKeys.filter((key) => selectedKeySet.has(key)).length;

    if (selectedCount === 0) return 'none';
    return selectedCount === selectableKeys.length ? 'all' : 'some';
  }, [selectableKeys, selectedKeySet]);

  /**
   * Select-all covers the current page only — the table never sees rows it wasn't
   * given. Selections made on other pages are preserved either way.
   */
  const toggleAll = useCallback(() => {
    const { selectedKeys: current, selectedKeySet: currentSet, selectableKeys: pageSelectable } = latest.current;
    const pageKeySet = new Set(pageSelectable);
    const allSelected = pageSelectable.length > 0 && pageSelectable.every((key) => currentSet.has(key));

    rangeAnchorKey.current = null;

    if (allSelected) {
      commit(current.filter((key) => !pageKeySet.has(key)));
      return;
    }

    const merged = [...current];
    pageSelectable.forEach((key) => {
      if (!currentSet.has(key)) merged.push(key);
    });

    commit(merged);
  }, [commit]);

  /** Clears the entire selection, including keys held from other pages. */
  const clearSelection = useCallback(() => {
    rangeAnchorKey.current = null;
    commit([]);
  }, [commit]);

  return {
    selectedKeySet,
    selectAllState,
    toggleRow,
    toggleAll,
    clearSelection,
    hasSelectableRows: selectableKeys.length > 0,
  };
};
